import { useEffect } from "react";
import { Form, Input, Modal, Select, Typography } from "antd";

import {
  COLLECTIONS_CALL_OUTCOME_LABELS,
  COLLECTIONS_CALL_TASK_TYPES,
  CollectionsCallOutcome,
  type CollectionsTask,
} from "@fitness-app/data-models/entities/CollectionsCase";

import { taskLabel } from "../collectionsLabels";

interface CompleteTaskModalProps {
  task: CollectionsTask | null;
  loading?: boolean;
  onCancel: () => void;
  onSubmit: (values: { outcome?: string | null; note?: string }) => void;
}

const OUTCOME_OPTIONS = Object.values(CollectionsCallOutcome).map((outcome) => ({
  value: outcome,
  label: COLLECTIONS_CALL_OUTCOME_LABELS[outcome],
}));

/**
 * Closing a task is where the human result enters the case: a call gets one of
 * the outcomes from the brief (and the outcome steers what happens next), any
 * other task gets a free note.
 */
const CompleteTaskModal = ({ task, loading, onCancel, onSubmit }: CompleteTaskModalProps) => {
  const [form] = Form.useForm<{ outcome?: string; note?: string }>();
  const isCall = task ? COLLECTIONS_CALL_TASK_TYPES.includes(task.type) : false;

  useEffect(() => {
    if (task) {
      form.resetFields();
    }
  }, [task, form]);

  return (
    <Modal
      open={task !== null}
      title={task ? `Zamknij: ${taskLabel(task.type)}` : ""}
      okText="Wykonano"
      cancelText="Anuluj"
      confirmLoading={loading}
      onCancel={onCancel}
      onOk={() =>
        void form
          .validateFields()
          .then((values) => onSubmit({ outcome: values.outcome ?? null, note: values.note?.trim() || undefined }))
          .catch(() => undefined)
      }
      destroyOnHidden
    >
      {isCall ? (
        <Typography.Paragraph type="secondary">
          Wynik rozmowy ustawia sytuację klientki i decyduje o kolejnym kroku: brak odpowiedzi po telefonie #1 planuje
          telefon #2 za 3 dni.
        </Typography.Paragraph>
      ) : null}
      <Form form={form} layout="vertical">
        {isCall ? (
          <Form.Item name="outcome" label="Wynik rozmowy" rules={[{ required: true, message: "Wybierz wynik" }]}>
            <Select options={OUTCOME_OPTIONS} showSearch optionFilterProp="label" />
          </Form.Item>
        ) : null}
        <Form.Item name="note" label="Notatka" extra="Co ustalono, co klientka powiedziała. Trafia na oś czasu sprawy.">
          <Input.TextArea rows={3} maxLength={2000} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CompleteTaskModal;
